/**
 * User Profile Service
 *
 * Handles profile operations via API Gateway REST API.
 * Uses AuthenticatedApiClient for all authenticated requests.
 *
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5, 6.6
 */

import { AuthenticatedApiClient } from './authenticated-api-client';
import type { UserProfile, UserProfileMutable } from '../types';

export class UserProfileService {
    private client: AuthenticatedApiClient;

    constructor() {
        this.validateEnvironment();
        this.client = new AuthenticatedApiClient();
    }

    private validateEnvironment(): void {
        if (!import.meta.env.VITE_API_GATEWAY_URL) {
            throw new Error('VITE_API_GATEWAY_URL environment variable is not set');
        }
    }

    async getProfile(): Promise<UserProfile> {
        const response = await this.client.apiRequest('/profile', { method: 'GET' });
        return response.json();
    }

    async updateProfile(fields: Partial<UserProfileMutable>): Promise<UserProfile> {
        try {
            const response = await this.client.apiRequest('/profile', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(fields),
            });
            return response.json();
        } catch (err) {
            // Surface duplicate-username 409 as a clear message
            if (err instanceof Error && err.message.includes('409')) {
                throw new Error('Username is already taken');
            }
            throw err;
        }
    }

    async deductCredits(amount: number): Promise<UserProfile> {
        const response = await this.client.apiRequest('/profile/credits/deduct', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ credits: amount }),
        });
        return response.json();
    }
}
